import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import { slugify } from 'utils/string-helpers';
import LoadingSpinner from 'components/LoadingSpinner';
import { Message } from 'components/shared';
import useUpdateAttributes from 'hooks/useUpdateAttributes';
import Zoom from '@material-ui/core/Zoom';
import { BottomToolbar } from './components';
import { getAttributes, getCellFormat, getColumns } from './utils';
import { TableHeader } from '../common/components';
import useSorting from '../common/hooks';
import TypographyWithTooltip from '../../../shared/TypographyWithTooltip';

const useStyles = makeStyles(theme => ({
  row: {
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: '0',
      textAlign: 'right',
      '&:first-child': {
        maxWidth: theme.customStyles.comparativeFirstRow.maxWidth,
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow,
        textAlign: 'left'
      },
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },
  negative: {
    color: theme.palette.error.main
  }
}));

const ComparativeStatsTable = ({
  state,
  widget,
  dashboardData,
  dispatchCannedDashboardList,
  setQueryParam,
  queryParam,
  setTableExportData,
  forwardRef
}) => {
  const [benchmarks, setBenchmarks] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [onAttributesUpdateSubmit, hasDataChanged, setHasDataChanged] = useUpdateAttributes({
    widget,
    dashboardData,
    dispatchCannedDashboardList
  });

  const { data: response, isLoading, isError, error } = state;

  const { overlay } = widget && widget;
  const { widgetTitle } = overlay;
  const { sortBy, sortOrder, getColumnSorting, onColumnSort } = useSorting();
  const classes = useStyles();

  useEffect(() => {
    if (isError || isLoading || !response) return;

    const updatedBenchmarks = response.benchmarks;
    const updatedAttributes = getAttributes(response.data, updatedBenchmarks);

    setBenchmarks(updatedBenchmarks);
    setAttributes(updatedAttributes);
    setTableExportData({
      title: widgetTitle && slugify(widgetTitle),
      columns: getColumns(updatedBenchmarks),
      rows: updatedAttributes
    });
  }, [response, isLoading, isError, setTableExportData, widgetTitle]);

  useEffect(() => {
    if (!hasDataChanged) return;
    setQueryParam(prevState => ({
      ...prevState,
      request: `${queryParam.request}1`
    }));
    setHasDataChanged(false);
  }, [hasDataChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (isError) {
    return <Message type="error" text={error} />;
  }

  const columns = getColumns(benchmarks);

  const renderSingleRow = row =>
    columns.map(column => {
      const { classList } = getCellFormat(column.key, row[column.key]);
      return (
        <TableCell key={`${column.key}-${row.id}`}>
          <TypographyWithTooltip
            variant="subtitle1"
            tooltipProps={{
              value: row[column.key],
              placement: 'right',
              TransitionComponent: Zoom,
              arrow: true
            }}
          >
            <span className={classList.map(c => classes[c]).join(' ')}>{row[column.key]}</span>
          </TypographyWithTooltip>
        </TableCell>
      );
    });

  const renderBody = () => {
    const sortedRows = attributes.sort(getColumnSorting(sortBy, sortOrder));
    return sortedRows.map(row => (
      <TableRow key={row.attribute} className={classes.row}>
        {renderSingleRow(row)}
      </TableRow>
    ));
  };

  return (
    <>
      <Table ref={forwardRef}>
        <TableHeader columns={columns} onColumnSort={onColumnSort} />
        <TableBody>{renderBody()}</TableBody>
      </Table>
      <BottomToolbar
        attributes={attributes}
        dashboardData={dashboardData}
        onAttributesUpdateSubmit={onAttributesUpdateSubmit}
        setHasDataChanged={setHasDataChanged}
      />
    </>
  );
};

ComparativeStatsTable.propTypes = {
  dashboardData: PropTypes.shape({}).isRequired,
  dispatchCannedDashboardList: PropTypes.func.isRequired,
  widget: PropTypes.shape({
    title: PropTypes.string,
    colSpan: PropTypes.number.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dateRangeType: PropTypes.string,
    isTitleDisplayed: PropTypes.bool.isRequired,
    isDateDisplayed: PropTypes.bool.isRequired,
    isLegendDisplayed: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    masterObjectId: PropTypes.number.isRequired,
    masterObjectTypeId: PropTypes.number,
    overlay: PropTypes.shape({
      baseWidget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        widgetType: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }),
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }),
  setQueryParam: PropTypes.func,
  queryParam: PropTypes.shape({
    request: PropTypes.string
  }),
  setTableExportData: PropTypes.func,
  forwardRef: PropTypes.shape({})
};

ComparativeStatsTable.defaultProps = {
  setTableExportData: null,
  state: {},
  setQueryParam: null,
  queryParam: {},
  widget: {},
  forwardRef: null
};

export default ComparativeStatsTable;
